import React from 'react';
import { RefreshCw, Maximize2 } from 'lucide-react';
import { colors } from '../../styles/tokens';
import { AIStatusLoader } from '../loading';
import { formatRelativeTime } from '../../utils/dateUtils';
import type { Lead } from '../../types';

export interface TableCellProps {
  lead: Lead;
  column: {
    key: string;
    name: string;
    width: number;
    isSegment?: boolean;
    isSystemColumn?: boolean;
  };
  columnIndex: number;
  cellValue: string;
  isLoading: boolean;
  isUrl: boolean;
  isEmail: boolean;
  isNameColumn: boolean;
  displayUrl: string | null | undefined;
  isUnknown: boolean;
  isSegmentColumn: boolean;
  isMonitoredColumn: boolean;
  isMatch: boolean;
  isMiss: boolean;
  isInterim: boolean;
  rowHasSelection: boolean;
  hasEnrichmentData: boolean;
  hasReasoning: boolean;
  dataTour?: string;
  // Event handlers
  onMouseDown: (e: React.MouseEvent) => void;
  onMouseEnter: () => void;
  onMouseUp: () => void;
  onClick: (e: React.MouseEvent) => void;
  onContextMenu?: (e: React.MouseEvent) => void;
  onExpandEnrichment: (e: React.MouseEvent) => void;
  onExpandMatch: (e: React.MouseEvent) => void;
}

/**
 * Individual table cell with support for different data types
 */
export const TableCell: React.FC<TableCellProps> = ({
  lead,
  column,
  columnIndex,
  cellValue,
  isLoading,
  isUrl,
  isEmail,
  isNameColumn,
  displayUrl,
  isUnknown,
  isSegmentColumn,
  isMonitoredColumn,
  isMatch,
  isMiss,
  isInterim,
  rowHasSelection,
  hasEnrichmentData,
  hasReasoning,
  dataTour,
  onMouseDown,
  onMouseEnter,
  onMouseUp,
  onClick,
  onContextMenu,
  onExpandEnrichment,
  onExpandMatch,
}) => {
  const hasDisplayUrl = isNameColumn && displayUrl;
  const isFirstColumn = columnIndex === 0;

  // Build cell className
  const cellClassName = [
    column.isSegment ? '' : 'truncate',
    'flex items-center min-h-0',
    'box-border py-2 px-4 text-[13px] select-none cursor-pointer relative'
  ].filter(Boolean).join(' ');

  // Build dynamic cell styles
  const cellStyle: React.CSSProperties = {
    width: `${column.width}px`,
    minWidth: `${column.width}px`,
    maxWidth: `${column.width}px`,
    color: colors.neutral[800],
    borderRight: `1px solid ${colors.neutral[100]}`,
    transition: 'background-color 80ms ease-out, width 150ms ease-out, min-width 150ms ease-out, max-width 150ms ease-out'
  };

  // Freeze first column
  if (isFirstColumn) {
    cellStyle.position = 'sticky';
    cellStyle.left = 40;
    cellStyle.zIndex = 2;
    cellStyle.boxShadow = '2px 0 4px rgba(0, 0, 0, 0.03)';
    cellStyle.backgroundColor = rowHasSelection ? colors.neutral[50] : 'white';
  }

  // Unknown values background
  if (isUnknown) {
    cellStyle.backgroundColor = colors.neutral[50];
  }

  // Render cell content based on type
  const renderCellContent = () => {
    // Loading state
    if (isLoading) {
      return <AIStatusLoader syncKey={`${column.key}:${lead.id}`} />;
    }

    // URL cell
    if (isUrl) {
      return (
        <a
          href={cellValue}
          target="_blank"
          rel="noopener noreferrer"
          className="text-gray-900 hover:text-gray-700 underline cursor-pointer"
          onClick={(e) => e.stopPropagation()}
        >
          {cellValue}
        </a>
      );
    }

    // Email cell
    if (isEmail) {
      return (
        <span className="text-gray-700">
          {cellValue}
        </span>
      );
    }

    // Name column with favicon
    if (hasDisplayUrl) {
      return (
        <a
          href={displayUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="w-7 h-7 min-w-7 min-h-7 flex-shrink-0 flex items-center justify-center bg-neutral-50 rounded-[5px] overflow-hidden">
            <img
              src={`https://www.google.com/s2/favicons?sz=64&domain=${new URL(displayUrl).hostname}`}
              alt=""
              className="w-[18px] h-[18px] object-contain"
              onError={(e) => {
                const target = e.currentTarget;
                target.style.display = 'none';
              }}
            />
          </div>
          <span className="text-primary-600 hover:text-primary-800 hover:underline font-semibold">
            {cellValue}
          </span>
        </a>
      );
    }

    // Monitored column
    if (isMonitoredColumn) {
      return (
        <div className="flex items-center justify-center w-full">
          {lead.sourceType === 'monitor' ? (
            <span className="flex items-center gap-1.5 text-xs text-neutral-600">
              <RefreshCw className="w-3 h-3" />
              {formatRelativeTime(lead.createdAt)}
            </span>
          ) : (
            <span className="text-neutral-400">-</span>
          )}
        </div>
      );
    }

    // Segment column with badge
    if (isSegmentColumn) {
      return (
        <div className="flex items-center justify-center w-full">
          <span className={`text-[11px] font-medium px-2.5 py-1 rounded-full cursor-pointer transition-all ${
            isMatch
              ? 'text-emerald-700 bg-emerald-50/60 hover:bg-emerald-100'
              : isMiss
                ? 'text-rose-600 bg-rose-50/60 hover:bg-rose-100'
                : 'text-neutral-500 bg-neutral-100/60 hover:bg-neutral-200'
          }`}>
            {cellValue}
          </span>
        </div>
      );
    }

    // Name column without URL (with possible interim badge)
    if (isNameColumn && !isUnknown) {
      // Try to get URL from lead data
      const companyUrl = lead.data?.url || lead.data?.URL || lead.data?.website || lead.data?.Website;

      return (
        <div className="flex items-center gap-2 min-w-0">
          {isInterim && (
            <span className="text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded font-medium flex-shrink-0">
              Preview
            </span>
          )}
          {companyUrl && (() => {
            try {
              const hostname = new URL(companyUrl).hostname;
              return (
                <div className="w-5 h-5 min-w-5 min-h-5 flex-shrink-0 flex items-center justify-center bg-neutral-100 rounded overflow-hidden">
                  <img
                    src={`https://www.google.com/s2/favicons?sz=64&domain=${hostname}`}
                    alt=""
                    className="w-3.5 h-3.5 object-contain"
                    onError={(e) => {
                      const target = e.currentTarget;
                      target.style.display = 'none';
                    }}
                  />
                </div>
              );
            } catch {
              return null;
            }
          })()}
          <span className="font-medium text-neutral-900 text-[13px] truncate">
            {cellValue}
          </span>
        </div>
      );
    }

    // Default text cell
    return (
      <span className={`${isNameColumn ? 'font-medium text-neutral-900' : ''} ${isUnknown ? 'text-neutral-400 italic text-[13px]' : ''}`}>
        {isUnknown ? 'No data' : cellValue}
      </span>
    );
  };

  return (
    <div
      data-cell-id={`${lead.id}::${column.key}`}
      data-row-id={lead.id}
      data-col-id={column.key}
      data-tour={dataTour}
      className={cellClassName}
      style={cellStyle}
      onMouseDown={onMouseDown}
      onMouseEnter={onMouseEnter}
      onMouseUp={onMouseUp}
      onClick={onClick}
      onMouseLeave={(e) => e.stopPropagation()}
      onContextMenu={onContextMenu}
    >
      {renderCellContent()}

      {/* Expand icon overlay for enriched cells */}
      {!column.isSegment && !column.name.toLowerCase().includes('confidence') && !isUnknown && hasEnrichmentData && (
        <div
          className="absolute right-1 top-1/2 -translate-y-1/2 opacity-0 hover:opacity-100 transition-opacity cursor-pointer p-1 rounded-sm flex items-center justify-center pointer-events-auto z-10"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onExpandEnrichment(e);
          }}
        >
          <Maximize2 size={14} className="text-neutral-500" />
        </div>
      )}

      {/* Expand icon for segment columns with company reasoning */}
      {column.isSegment && hasReasoning && (
        <div
          className="absolute right-1 top-1/2 -translate-y-1/2 opacity-0 hover:opacity-100 transition-opacity cursor-pointer p-1 rounded-sm flex items-center justify-center pointer-events-auto z-10"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
          }}
          onClick={onExpandMatch}
        >
          <Maximize2 size={14} className="text-neutral-500" />
        </div>
      )}
    </div>
  );
};

TableCell.displayName = 'TableCell';
